// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Timeline, TimelineConnector, TimelineItem, TimelineOppositeContent, TimelineSeparator } from '@mui/lab';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
import { CommonIconEventTimeline } from '~/assets/svgComponents';
// Others
import { formatDate, formatTimeTo12h } from '~/utils/helper/common';
import { IOrderDetail } from '~/utils/interface/order';
import { IQuoteDetail } from '~/utils/interface/quotes';
import { DateFormatEnum } from '~/utils/enum/common';
import { AMBER_500, BLUE_500, GRAY_500, GREEN_600, INDIGO_500, PINK_700 } from '~/utils/constants/color';
// Styles, images, icons
import styles from './EventTineLineQuoteDetail.module.scss';
import { icons } from '~/assets';

type Props = {
  orderDetail?: IOrderDetail;
  detailQuote?: IQuoteDetail;
};

const cx = classNames.bind(styles);

const EventTineLineQuoteDetail = (props: Props) => {
  //#region Destructuring Props
  const { detailQuote, orderDetail } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/quotes', 'common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [data, setData] = useState<IQuoteDetail | IOrderDetail | undefined>(detailQuote || orderDetail);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    setData(detailQuote || orderDetail);
  }, [detailQuote, orderDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  //#endregion Handle Function

  return (
    <Stack id='eventTineLineQuoteDetailComponent' className={cx('container')} gap={6}>
      <BaseLabel styleType='title' label={t('form_quotes_title_event_timeline')} icon={icons.commonIconEventTimeLine} />

      <Timeline className={cx('timeline')}>
        <TimelineItem>
          <TimelineSeparator>
            {/* <TimelineConnector className={cx('viewDotWhite')} /> */}
            <CommonIconEventTimeline fill={GRAY_500} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_quotes_title_earliest_delivery')}</Typography>

              <Stack gap={2} direction={'row'}>
                <Box className={cx('viewDate')}>
                  {formatDate(data?.earliestDeliveryDate, DateFormatEnum.MM_DD_YYYY)}
                </Box>

                {data?.earliestDeliveryTime && (
                  <Box className={cx('viewDate')}>{formatTimeTo12h(data?.earliestDeliveryTime)}</Box>
                )}
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            {/* <TimelineConnector className={cx('viewDot')} /> */}
            <CommonIconEventTimeline fill={AMBER_500} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_quotes_title_delivery_scheduled')}</Typography>

              <Stack direction={'row'} gap={2}>
                {data?.deliveryScheduledDate && (
                  <Box className={cx('viewDate')}>
                    {formatDate(data?.deliveryScheduledDate, DateFormatEnum.MM_DD_YYYY)}
                  </Box>
                )}
                {data?.deliveryScheduledTime && (
                  <Box className={cx('viewDate')}>{formatTimeTo12h(data?.deliveryScheduledTime)}</Box>
                )}
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            {/* <TimelineConnector className={cx('viewDot')} /> */}
            <CommonIconEventTimeline fill={BLUE_500} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_quotes_title_event_start')}</Typography>

              <Stack direction={'row'} gap={2}>
                <Box className={cx('viewDate')}>{formatDate(data?.startDate, DateFormatEnum.MM_DD_YYYY)}</Box>

                {data?.startTime && <Box className={cx('viewDate')}>{formatTimeTo12h(data?.startTime)}</Box>}
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            {/* <TimelineConnector className={cx('viewDot')} /> */}
            <CommonIconEventTimeline fill={GREEN_600} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_quotes_title_event_end')}</Typography>

              <Stack direction={'row'} gap={2}>
                <Box className={cx('viewDate')}>{formatDate(data?.endDate, DateFormatEnum.MM_DD_YYYY)}</Box>

                {data?.endTime && <Box className={cx('viewDate')}>{formatTimeTo12h(data?.endTime)}</Box>}
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            {/* <TimelineConnector className={cx('viewDot')} /> */}
            <CommonIconEventTimeline fill={PINK_700} />
            <TimelineConnector className={cx('viewDot')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_quotes_title_pickup_scheduled')}</Typography>

              <Stack direction={'row'} gap={2}>
                {data?.pickupScheduledDate && (
                  <Box className={cx('viewDate')}>
                    {formatDate(data?.pickupScheduledDate, DateFormatEnum.MM_DD_YYYY)}
                  </Box>
                )}

                {data?.pickupScheduledTime && (
                  <Box className={cx('viewDate')}>{formatTimeTo12h(data?.pickupScheduledTime)}</Box>
                )}
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>

        <TimelineItem>
          <TimelineSeparator>
            {/* <TimelineConnector className={cx('viewDot')} /> */}
            <CommonIconEventTimeline fill={INDIGO_500} />
            <TimelineConnector className={cx('viewDotWhite')} />
          </TimelineSeparator>

          <TimelineOppositeContent>
            <Stack className={cx('viewContent')}>
              <Typography className={cx('titleItem')}>{t('form_quotes_title_latest_pickup')}</Typography>

              <Stack direction={'row'} gap={2}>
                <Box className={cx('viewDate')}>{formatDate(data?.latestPickupDate, DateFormatEnum.MM_DD_YYYY)}</Box>

                {data?.latestPickupTime && (
                  <Box className={cx('viewDate')}>{formatTimeTo12h(data?.latestPickupTime)}</Box>
                )}
              </Stack>
            </Stack>
          </TimelineOppositeContent>
        </TimelineItem>
      </Timeline>
    </Stack>
  );
};

export default EventTineLineQuoteDetail;
